<template>
    <StripeSection />

    <div class="pt-6 text-sm font-medium text-gray-370 dark:text-white">
        Please upload your card details
    </div>

    <div class="pt-3 text-xs text-gray-280">
        We’ve already verified your identity using your passport. Now we just need to confirm your card
    </div>

    <div class="pt-2 text-xs text-gray-280">
        1. Front side of the card (your first and last name & last 4 digits) <br/>
        2. Back side of the card
    </div>

    <div class="pt-2 text-xs text-gray-280">
        Make sure the name on your card matches your verified ID
    </div>

    <div class="pt-6 flex flex-col gap-y-3">
        <div class="text-sm text-gray-370 dark:text-white font-medium">
            Front of Card
        </div>

        <UploadFile
            v-model:files="frontFiles"
            title="Select File or Drag & Drop"
            fileSizeText="recommend size 4MB"
            :maxSize="MAX_FILE_SIZE_MB"
            :truncateLength="truncateLength"
        />
    </div>

    <div class="pt-5 flex flex-col gap-y-3">
        <div class="text-sm text-gray-370 dark:text-white font-medium">
            Back of Card
        </div>

        <UploadFile
            v-model:files="backFiles"
            title="Select File or Drag & Drop"
            fileSizeText="recommend size 4MB"
            :maxSize="MAX_FILE_SIZE_MB"
            :truncateLength="truncateLength"
        />
    </div>

    <div class="w-full mt-7 pt-4 flex justify-end gap-x-5 border-t border-gray-100 dark:border-blue-720">
        <PrimaryBtn
            class="max-mob:w-full"
            borderOutlinedStyle="border-gray-370 dark:border-gray-100"
            textOutlinedStyle="text-gray-370 dark:text-gray-100"
            outlined
            @click="prevStep"
        >
            Go Back
        </PrimaryBtn>

        <PrimaryBtn
            class="max-mob:w-full"
            :loading="verificationUserStore.actionLoading"
            :disabled="disableNextStep || disableReviewAction"
            @click="submitForm"
        >
            Next
        </PrimaryBtn>
    </div>
</template>

<script setup>
import { inject, computed } from 'vue'

import StripeSection from '@/views/verification/sections/StripeSection.vue'
import UploadFile from '@/components/uploadFile/UploadFile.vue'
import PrimaryBtn from '@/components/buttons/PrimaryBtn.vue'

import { getMbFromBytes } from '@/helpers/app-helper'

import { useVerificationUserStore } from '@/store/call-tracking/verificationUserStore'

import { viewPorts } from '@/constants/viewPorts'
import { STATUSES } from '@/constants/verification'

const emits = defineEmits(['nextStep', 'prevStep', 'goToReview'])

const currentViewSize = inject('currentViewSize')

const verificationUserStore = useVerificationUserStore()

const MAX_FILE_SIZE_MB = 4

const isMobView = computed(() => viewPorts.mob >= currentViewSize.value)

const truncateLength = computed(() => (isMobView.value ? 30 : 50))

const frontFiles = computed({
    get() {
        return verificationUserStore.card_files.front_card
    },
    set(value) {
        verificationUserStore.updateCardFiles('front_card', value)
    },
})

const backFiles = computed({
    get() {
        return verificationUserStore.card_files.back_card
    },
    set(value) {
        verificationUserStore.updateCardFiles('back_card', value)
    },
})

const frontDone = computed(() => verificationUserStore.verificationSteps?.paymentStep?.frontCardDone)
const backDone = computed(() => verificationUserStore.verificationSteps?.paymentStep?.backCardDone)

const hasOversizedFrontFiles = computed(() => frontFiles.value.some((file) => getMbFromBytes(file.size) > MAX_FILE_SIZE_MB))
const hasOversizedBackFiles = computed(() => backFiles.value.some((file) => getMbFromBytes(file.size) > MAX_FILE_SIZE_MB))

const filesHaveBeenAdded = computed(() => (frontDone.value || frontFiles.value.length > 0) && (backDone.value || backFiles.value.length > 0))
const someFilesHaveBeenAdded = computed(() => frontFiles.value.length > 0 || backFiles.value.length > 0)

const disableNextStep = computed(() => hasOversizedFrontFiles.value || hasOversizedBackFiles.value || !filesHaveBeenAdded.value || verificationUserStore.actionLoading || !verificationUserStore.stripeIsVerify)
const disableReviewAction = computed(() => hasOversizedFrontFiles.value || hasOversizedBackFiles.value || (!someFilesHaveBeenAdded.value && verificationUserStore.paymentStepStatus === STATUSES.rejected.id))

async function submitForm() {
    if (disableNextStep.value || disableReviewAction.value) {
        return
    }

    if (verificationUserStore.paymentStepDone && !frontFiles.value.length && !backFiles.value.length) {
        nextStep()

        return
    }

    const { success } = await verificationUserStore.updateVerificationDataCard()

    if (success) {
        nextStep()
    }
}

function nextStep() {
    emits('nextStep')
}

function prevStep() {
    emits('prevStep')
}
</script>
